import { Box, CircularProgress, Icon, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import NoteAddIcon from '@mui/icons-material/NoteAdd'
import EditIcon from '@mui/icons-material/Edit'
import UndoIcon from '@mui/icons-material/Undo'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ClearIcon from '@mui/icons-material/Clear'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import useBoolean from 'common/hooks/useBoolean'
import useTimeout from 'common/hooks/useTimeout'
import useFetchTimeout from 'common/hooks/useFetchTimeout'
import ApplicationConf from 'conf/ApplicationConf'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import moment from 'moment'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'
import { pulseGrow } from 'common/utils/AnimationsUtils'
import { AppActions } from 'app/appSlice'
import { Cached } from '@mui/icons-material'

const Action = ({
    icon,
    onClick,
    tooltip,
    color = 'inherit',
    pulse = false,
    'data-cy': dataCy,
}) => {
    const button = (
        <IconButton
            sx={{ fontSize: '32px', color, animation: pulse ? `${pulseGrow} 1s ease infinite` : undefined }}
            onClick={onClick}
            data-cy={dataCy}
        >
            <Icon fontSize='inherit'>{icon}</Icon>
        </IconButton>
    )
    if (isNil(tooltip)) return button
    return (
        <Tooltip title={tooltip}>
            {button}
        </Tooltip>
    )
}

Action.propTypes = {
    icon: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    tooltip: PropTypes.string,
    color: PropTypes.string,
    pulse: PropTypes.bool,
    'data-cy': PropTypes.string,
}

const Other = ({
    icon = '',
    tooltip = '',
    onClick = () => { },
    color,
}) => {
    return (
        <Tooltip title={tooltip}>
            <Icon sx={{ color: color || 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick}>{icon}</Icon>
        </Tooltip>
    )
}

Other.propTypes = {
    icon: PropTypes.string,
    tooltip: PropTypes.string,
    onClick: PropTypes.func,
    color: PropTypes.string,
}

const New = ({
    onClick = () => {},
    tooltip = 'Nouveau',
}) => {
    return (
        <Tooltip title={tooltip}>
            <NoteAddIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick}/>
        </Tooltip>
    )
}

New.propTypes = {
    onClick: PropTypes.func,
    tooltip: PropTypes.string,
}

const Refresh = ({
    onClick = () => {},
    tooltip = 'Actualiser',
    loading,
}) => {
    if (loading) {
        return <CircularProgress size='30px' thickness={5} disableShrink sx={{ color: 'white' }} />
    }
    return (
        <Tooltip title={tooltip}>
            <Cached sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick} />
        </Tooltip>
    )
}

Refresh.propTypes = {
    onClick: PropTypes.func,
    tooltip: PropTypes.string,
    loading: PropTypes.bool,
}

const Edit = ({
    onClick = () => { },
}) => {
    return (
        <Tooltip title='Editer'>
            <EditIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick}/>
        </Tooltip>
    )
}

Edit.propTypes = {
    onClick: PropTypes.func,
}

const Cancel = ({
    onClick = () => { },
}) => {
    return (
        <Tooltip title='Annuler'>
            <UndoIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick}/>
        </Tooltip>
    )
}

Cancel.propTypes = {
    onClick: PropTypes.func,
}

const Delete = ({
    onClick = () => { },
}) => {
    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)

    return (
        <>
            <Tooltip title='Supprimer'>
                <DeleteIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={open} />
            </Tooltip>
            <ConfirmModal
                isOpen={isOpen}
                title={'Êtes-vous sûr de vouloir supprimer cet élément ?'}
                onValidate={() => {
                    onClick()
                    close()
                }}
                onClose={close}
            />
        </>
    )
}

Delete.propTypes = {
    onClick: PropTypes.func,
}

const Save = ({
    onClick = () => { },
    pulse = false,
}) => {
    return (
        <Tooltip title='Sauvegarder'>
            <IconButton
                sx={{ fontSize: '38px', color: '#FFF', padding: '8px', animation: pulse ? `${pulseGrow} 1s ease infinite` : undefined }}
                onClick={onClick}
            >
                <SaveIcon fontSize='inherit'/>
            </IconButton>
        </Tooltip>
    )
}

Save.propTypes = {
    onClick: PropTypes.func,
    pulse: PropTypes.bool,
}

const Clock = () => {
    const [time, setTime] = useState([])
    const { reset } = useTimeout(() => {
        setTime(moment().format('DD/MM/YYYY HH:mm:ss'))
    }, 1000)

    useEffect(() => {
        reset()
    }, [reset, time])

    return (
        <Tooltip title={time}>
            <AccessTimeIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} />
        </Tooltip>
    )
}

const Clear = ({
    onClick = () => { },
}) => {
    return (
        <Tooltip title='Supprimer'>
            <ClearIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick} />
        </Tooltip>
    )
}

Clear.propTypes = {
    onClick: PropTypes.func,
}

const Upload = ({
    onClick = () => { },
}) => {
    return (
        <Tooltip title='Upload'>
            <FileUploadIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick} />
        </Tooltip>
    )
}

Upload.propTypes = {
    onClick: PropTypes.func,
}

const Download = ({
    onClick = () => { },
}) => {
    return (
        <Tooltip title='Download'>
            <FileDownloadIcon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={onClick} />
        </Tooltip>
    )
}

Download.propTypes = {
    onClick: PropTypes.func,
}

const Sound = () => {
    const dispatch = useDispatch()
    const {
        isMute,
    } = useSelector(store => ({
        isMute: store.app.isMute,
    }), shallowEqual)

    return (
        <Tooltip title='Couper le son'>
            <Icon sx={{ color: 'white', fontSize: '38px', cursor: 'pointer' }} onClick={() => dispatch(AppActions.toggleMute())}>
                {isMute ? 'volume_off' : 'volume_up'}
            </Icon>
        </Tooltip>
    )
}

const DataBankStatus = () => {
    const { data } = useFetchTimeout(ApplicationConf.dataBanks(), undefined, 30000, { skipLoadingState: true })

    const dataBanks = data?.dataBanks || []
    const hasAnyKo = dataBanks.some(db => db.status === 'KO')
    const allOk = dataBanks.length > 0 && dataBanks.every(db => db.status === 'OK')

    const iconColor = hasAnyKo ? PASTEL_COLOR.red : (allOk ? PASTEL_COLOR.green : PASTEL_COLOR.grey)

    const tooltipContent = (
        <Stack spacing={0.5} sx={{ p: 0.5 }}>
            <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                Banques de données
            </Typography>
            {dataBanks.length === 0 ? (
                <Typography variant='caption'>
                    Aucune information disponible
                </Typography>
            ) : (
                dataBanks.map((db, idx) => {
                    const isKo = db.status === 'KO'
                    const isOk = db.status === 'OK'
                    return (
                        <Box key={idx}>
                            <Typography variant='body2' component='div' sx={{ fontWeight: 'medium' }}>
                                {db.name} :
                                <Typography component='span' variant='body2' sx={{ color: isKo ? PASTEL_COLOR.redLight : (isOk ? PASTEL_COLOR.green : PASTEL_COLOR.grey), fontWeight: 'bold' }}>
                                    {isKo ? ' Indisponible (KO)' : (isOk ? ' OK' : ' En attente')}
                                </Typography>
                            </Typography>
                            {isKo && db.downSince && (
                                <Typography variant='caption' sx={{ color: PASTEL_COLOR.redLight, pl: 1.5 }}>
                                    Depuis le {moment(db.downSince).format('DD/MM/YYYY [à] HH:mm')}
                                </Typography>
                            )}
                        </Box>
                    )
                })
            )}
        </Stack>
    )

    return (
        <Tooltip title={tooltipContent} arrow>
            <Icon sx={{ color: iconColor, fontSize: '38px', cursor: 'pointer' }}>
                dns
            </Icon>
        </Tooltip>
    )
}

export {
    Action,
    Other,
    New,
    Edit,
    Cancel,
    Delete,
    Save,
    Clock,
    Clear,
    Upload,
    Download,
    Sound,
    Refresh,
    DataBankStatus,
}